import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";
import { useSupabaseAuth } from "../../Context/AuthContext/AuthContext";
import { useLeagueStore } from "../../zustandStore/leagueStore";

const WS_URL = process.env.REACT_APP_WEBSOCKET_URL || "wss://chat.rotobot.ai";

interface Message {
  type: "question" | "response";
  content: string;
}

interface SocketContextType {
  isConnected: boolean;
  messages: Message[];
  isLoading: boolean;
  sendMessage: (message: string, sendMessage: string | null) => void;
  startNewThread: (leagueId?:string | undefined,ownerId?:string | undefined) => void;
  connectionError: string | null;
  threadId: string | null;
  threadIdError: string | null;
  checkpointId: string | null;
  likeMessage: (
    likedStatus: boolean,
    checkpointId: string | null,
    threadId: string | null,
  ) => void;
  chartResponse: any | null; // Added chartResponse
  resetChartResponse: () => void; // Added reset function
  followUpQuestions: string[]; // Added follow-up questions array
  resetFollowUpQuestions: () => void; // Added reset function for follow-up questions
}

const SocketContext = React.createContext<SocketContextType | undefined>(
  undefined,
);

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [checkpointId, setCheckpointId] = useState<string | null>(null);
  const [threadIdError, setThreadIdError] = useState<string | null>(null);
  const [chartResponse, setChartResponse] = useState<any | null>(null); // New state for chart data
  const [followUpQuestions, setFollowUpQuestions] = useState<string[]>([]); // New state for follow-up questions
  const socketRef = useRef<WebSocket | null>(null);
  const messageBufferRef = useRef<string[]>([]);
  const bufferTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const MAX_RECONNECT_ATTEMPTS = 5;
  const { useAuthStore } = useSupabaseAuth();
  const location = useLocation();
  const { selectedLeague } = useLeagueStore();
  // Subscribe directly to the access token to prevent unnecessary re-renders
  const userAccessToken = useAuthStore((state: any) => {
    const user = state.user;
    return user?.access_token || 
           user?.session?.access_token || 
           (user?.user?.session && user?.user?.session?.access_token);
  });

  // console.log(chartResponse, "chartResponse");
  // console.log(messages, "messages");
  // Reset state when user navigates back to this page
  useEffect(() => {
    setMessages([]);
    setIsLoading(false);
    setConnectionError(null);
    setCheckpointId(null);
    setChartResponse(null); // Reset chart data on navigation
    setFollowUpQuestions([]); // Reset follow-up questions on navigation
    messageBufferRef.current = [];
  }, [location.pathname]);

  const resetChartResponse = useCallback(() => {
    setChartResponse(null);
  }, []);

  const resetFollowUpQuestions = useCallback(() => {
    setFollowUpQuestions([]);
  }, []);

  const flushMessageBuffer = useCallback(() => {
    if (messageBufferRef.current.length > 0) {
      const combinedMessage = messageBufferRef.current.join("");
      setMessages((prev) => [
        ...prev,
        { type: "response", content: combinedMessage },
      ]);
      messageBufferRef.current = [];
    }
  }, []);

  const scheduleBufferFlush = useCallback(() => {
    if (bufferTimeoutRef.current) {
      clearTimeout(bufferTimeoutRef.current);
    }
    bufferTimeoutRef.current = setTimeout(flushMessageBuffer, 100);
  }, [flushMessageBuffer]);

  const connectWebSocket = useCallback(() => {
    if (socketRef.current?.readyState === WebSocket.CONNECTING) {
      return socketRef.current; // Don't create new connection if already connecting
    }

    // Use the stable access token reference
    if (!userAccessToken) {
      console.error("No access token available for WebSocket authentication");
      setConnectionError("Authentication required. Please log in.");
      return null;
    }

    const socket = new WebSocket(`${WS_URL}/ws?token=${userAccessToken}`);
    socketRef.current = socket;

    socket.onopen = () => {
      console.log("✅ WebSocket Connected");
      setIsConnected(true);
      setConnectionError(null);
      reconnectAttemptsRef.current = 0; // Reset reconnection attempts on successful connection
    };

    socket.onmessage = (event) => {
      console.log("📩 Message Received:", event.data);
      try {
        const data = JSON.parse(event.data);

        if (data.type === "thread_info") {
          if (data.thread_id) {
            setThreadId(data.thread_id);
            setThreadIdError(null);
          }
          return;
        }

        if (data.type === "checkpoint_info") {
          console.log("📋 Checkpoint Info Received:", data.checkpoint_id);
          if (data.checkpoint_id) {
            setCheckpointId(data.checkpoint_id);
          }
          return;
        }

        if (data.type === "ai_response_chunk") {
          // Add only the content to the message buffer
          messageBufferRef.current.push(data.content);
          scheduleBufferFlush();
        } else if (data.type === "chart_data") {
          console.log("📊 Chart Data Received");
          setChartResponse(JSON.parse(data.content));
          flushMessageBuffer(); // Flush any pending messages
          setIsLoading(false); // Stop loading since this is the final response
        } else if (data.type === "follow_up") {
          console.log("🔄 Follow-up Question Received:", data.content);
          setFollowUpQuestions(prev => [...prev, data.content]);
        } else if (data.type === "stream_end") {
          flushMessageBuffer();
          setIsLoading(false);
        }
      } catch (e) {
        // Log parsing error but don't apply any fallback logic
        console.error("Failed to parse message:", e);
      }
    };

    socket.onerror = (error) => {
      console.error("❌ WebSocket Error:", error);
      setConnectionError("WebSocket error");
      setIsLoading(false);
    };

    socket.onclose = (event) => {
      console.warn("⚠️ WebSocket Disconnected:", event.reason);
      setIsConnected(false);
      setIsLoading(false);

      // Handle authentication-specific close codes
      if (event.code === 1008) {
        setConnectionError("Authentication failed. Please log in again.");
        reconnectAttemptsRef.current = MAX_RECONNECT_ATTEMPTS; // Stop reconnection attempts for auth failures
        return;
      }

      // Only attempt to reconnect if the component is still mounted and not an auth error
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        const reconnectDelay = Math.min(
          1000 * Math.pow(2, reconnectAttemptsRef.current),
          10000,
        );
        console.log(
          `Attempting to reconnect in ${reconnectDelay}ms... (Attempt ${reconnectAttemptsRef.current + 1}/${MAX_RECONNECT_ATTEMPTS})`,
        );

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }

        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttemptsRef.current += 1;
          connectWebSocket();
        }, reconnectDelay);

        setConnectionError(
          `Disconnected: Attempting to reconnect... (${reconnectAttemptsRef.current + 1}/${MAX_RECONNECT_ATTEMPTS})`,
        );
      } else {
        setConnectionError(
          "Connection failed after maximum reconnection attempts. Please refresh the page.",
        );
      }
    };

    return socket;
  }, [userAccessToken, flushMessageBuffer, scheduleBufferFlush]);

  useEffect(() => {
    const socket = connectWebSocket();

    return () => {
      // Cleanup function
      reconnectAttemptsRef.current = MAX_RECONNECT_ATTEMPTS; // Prevent further reconnection attempts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (bufferTimeoutRef.current) {
        clearTimeout(bufferTimeoutRef.current);
      }
      if (socket) {
        socket.close();
      }
    };
  }, [connectWebSocket]);

  const sendMessage = (message: string, threadId: string | null) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      // If not connected, try to reconnect before sending
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        connectWebSocket();
        setConnectionError("Reconnecting before sending message...");
        // Could implement a message queue here if needed
        return;
      }
      console.error("❌ Cannot send message: Not connected to WebSocket");
      setConnectionError("Cannot send message: Not connected");
      return;
    }
    console.log("📤 Sending message:", message);
    messageBufferRef.current = [];
    setCheckpointId(null);
    setChartResponse(null); // Reset chart data when sending a new message
    setFollowUpQuestions([]); // Reset follow-up questions when sending a new message
    // Clear all previous messages
    setMessages([{ type: "question", content: message }]);
    
    // Create JSON payload with required format and include league info if available
    const payload: any = {
      type: "chat",
      thread_id: threadId,
      content: message,
    };
    
    // Add league information if available
    if (selectedLeague?.leagueId && selectedLeague?.ownerId) {
      payload.league_id = selectedLeague.leagueId;
      payload.owner_id = selectedLeague.ownerId;
    }

    socketRef.current.send(JSON.stringify(payload));
    setIsLoading(true);
  };

  const startNewThread = useCallback((leagueId:string | undefined,ownerId:string | undefined) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      // If not connected, try to reconnect before sending
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        const socket = connectWebSocket();
        setConnectionError("Reconnecting before starting new thread...");

        // If socket was created, wait for connection to open before sending
        if (socket) {
          socket.addEventListener(
            "open",
            () => {
              // Create the message payload conditionally
              const payload: any = { type: "start_new_thread" };
              if (leagueId && ownerId) {
                payload.league_id = leagueId;
                payload.owner_id = ownerId;
              }
              
              socket.send(JSON.stringify(payload));
              console.log("📤 Starting new thread");
              // Reset all state values
              setThreadIdError(null);
              setCheckpointId(null);
              setChartResponse(null); // Reset chart data on new thread
              setFollowUpQuestions([]); // Reset follow-up questions on new thread
              setMessages([]);
              setIsLoading(false);
              messageBufferRef.current = [];
            },
            { once: true },
          );
        }
        return;
      }
      console.error("❌ Cannot start new thread: Not connected to WebSocket");
      setConnectionError("Cannot start new thread: Not connected");
      return;
    }

    // If already connected, send the message directly
    console.log("📤 Starting new thread");
    
    // Create the message payload conditionally
    const payload: any = { type: "start_new_thread" };
    if (leagueId && ownerId) {
      payload.league_id = leagueId;
      payload.owner_id = ownerId;
    }
    console.log("Starting new thread",payload)
    socketRef.current.send(JSON.stringify(payload));
    // Reset all state values
    setThreadIdError(null);
    setCheckpointId(null);
    setChartResponse(null); // Reset chart data on new thread
    setFollowUpQuestions([]); // Reset follow-up questions on new thread
    setMessages([]);
    setIsLoading(false);
    messageBufferRef.current = [];
  }, [connectWebSocket, reconnectAttemptsRef]);

  const likeMessage = useCallback(
    (
      likedStatus: boolean,
      checkpointId: string | null,
      threadId: string | null,
    ) => {
      if (
        !socketRef.current ||
        socketRef.current.readyState !== WebSocket.OPEN
      ) {
        // If not connected, try to reconnect before sending rating
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          const socket = connectWebSocket();
          setConnectionError("Reconnecting before sending rating...");

          // If socket was created, wait for connection to open before sending
          if (socket) {
            socket.addEventListener(
              "open",
              () => {
                if (!threadId || !checkpointId) {
                  console.error(
                    "❌ Cannot rate message: Thread ID or Checkpoint ID is missing",
                  );
                  return;
                }

                socket.send(
                  JSON.stringify({
                    type: "like_message",
                    thread_id: threadId,
                    checkpoint_id: checkpointId,
                    liked: likedStatus,
                  }),
                );
                console.log(
                  `📤 Rating message: ${likedStatus ? "Like" : "Dislike"}`,
                );
              },
              { once: true },
            );
          }
          return;
        }
        console.error("❌ Cannot rate message: Not connected to WebSocket");
        setConnectionError("Cannot rate message: Not connected");
        return;
      }

      // Validate required IDs are present
      if (!threadId || !checkpointId) {
        console.error(
          "❌ Cannot rate message: Thread ID or Checkpoint ID is missing",
        );
        return;
      }
      // If already connected, send the rating directly
      console.log(`📤 Rating message: ${likedStatus ? "Like" : "Dislike"}`);
      socketRef.current.send(
        JSON.stringify({
          type: "like_message",
          thread_id: threadId,
          checkpoint_id: checkpointId,
          liked: likedStatus,
        }),
      );
    },
    [connectWebSocket, reconnectAttemptsRef, threadId, checkpointId],
  );

  return (
    <SocketContext.Provider
      value={{
        isConnected,
        messages,
        isLoading,
        sendMessage,
        startNewThread,
        connectionError,
        threadId,
        threadIdError,
        checkpointId,
        likeMessage,
        chartResponse, // Expose chart data
        resetChartResponse, // Expose reset function
        followUpQuestions, // Expose follow-up questions array
        resetFollowUpQuestions, // Expose reset function for follow-up questions
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};
